import { asyncFrame } from '@utils/asyncFrame';
import * as D3Array from 'd3-array';
import * as D3TimeFormat from 'd3-time-format';
import * as moment from 'moment';
import usedLocales from '../../data/locales';
import { last, max } from '../../utils/helpers';
import type { DataProps, InfoValues, ParsedDataProps, XDomain, XRange, YDomain } from './types';
import { type DatePresets, HighlightLinePresets } from './types';

export const datePresets = (xDomain: XDomain): Date[] => [
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(1, 'weeks').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(1, 'months').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(6, 'months').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(12, 'months').toDate()])),
  new Date(max([xDomain[0], moment().startOf('year').subtract(1, 'day').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(3, 'years').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.max(xDomain)).subtract(10, 'years').toDate()])),
  new Date(max([xDomain[0], moment(D3Array.min(xDomain)).toDate()])),
];

export const timeFormatter = (locale: string, date: Date): string => {
  const localeDefinition = usedLocales[locale];
  return D3TimeFormat.timeFormat(localeDefinition.date)(date);
};

export const timeParser = (dateString: string): Date => D3TimeFormat.timeParse('%Y-%m-%d')(dateString) as any as Date;

export const assembleChartTitle = (currency: string, header: string, detailXDomain: XDomain, locale: string): string =>
  `${header} ${timeFormatter(locale, detailXDomain[0])} - ${timeFormatter(locale, detailXDomain[1])} in ${currency}`;

export const isLeapYear = (year: number): boolean => {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
};

export const calcPerAnnoValues = (detailXDomain: XDomain, lastDayPerformance: number): number | null => {
  const [startDate, endDate] = D3Array.extent(detailXDomain);

  if (!startDate || !endDate) {
    return null;
  }

  const numberOfDays = Math.ceil(Math.abs(startDate.valueOf() - endDate.valueOf()) / (1000 * 3600 * 24));

  if (numberOfDays === 366) {
    if (isLeapYear(startDate.getFullYear()) && startDate.getMonth() < 2) {
      return null;
    }
    if (isLeapYear(endDate.getFullYear()) && (endDate.getMonth() > 1 || (endDate.getMonth() === 1 && endDate.getDate() === 29))) {
      return null;
    }
  }

  const x = numberOfDays / 365;

  if (x <= 1) {
    return null;
  }

  return Math.round(((1 + lastDayPerformance) ** (1 / x) - 1) * 100 * 100) / 100;
};

export const calcInfoValues = (detailData: ParsedDataProps[], detailXDomain: XDomain): InfoValues => {
  const infoPerformance = detailData.length > 0 ? last(detailData).performance - 100 : null;
  const infoPerformancePerAnno = calcPerAnnoValues(detailXDomain, infoPerformance / 100);

  const infoBenchmark = detailData.length > 0 ? last(detailData).benchmark - 100 : null;
  const infoBenchmarkPerAnno = calcPerAnnoValues(detailXDomain, infoBenchmark / 100);

  return {
    performance: typeof infoPerformance === 'number' ? Math.round(infoPerformance * 100) / 100 : null,
    performancePA: typeof infoPerformancePerAnno === 'number' ? Math.round(infoPerformancePerAnno * 100) / 100 : null,
    benchmark: typeof infoBenchmark === 'number' ? Math.round(infoBenchmark * 100) / 100 : null,
    benchmarkPA: typeof infoBenchmarkPerAnno === 'number' ? Math.round(infoBenchmarkPerAnno * 100) / 100 : null,
  };
};

export const hasBenchmarkData = (data: any[]): boolean => data.every((entry) => entry.benchmark != null && entry.benchmark > 0);

export const parseData = (data: DataProps[]): ParsedDataProps[] | null => {
  return data ? data.map((d: DataProps) => ({ ...d, date: timeParser(d.date) })) : null;
};

export const percentifyData = (data: ParsedDataProps[], hasBenchmark: boolean): ParsedDataProps[] | null =>
  data
    ? data.map((d: ParsedDataProps) => {
        const prepared: ParsedDataProps = {
          date: d.date,
          performance: d.performance * 100,
        };
        if (hasBenchmark) {
          prepared.benchmark = d.benchmark * 100;
        }
        return prepared;
      })
    : null;

export const nearestDateInPast = (dataArray: ParsedDataProps[], referenceDate: Date): ParsedDataProps | null => {
  if (!dataArray || dataArray.length === 0) {
    return null;
  }
  return dataArray.reduce((prev, curr) =>
    Math.abs(referenceDate.valueOf() - curr.date.valueOf()) < Math.abs(referenceDate.valueOf() - prev.date.valueOf()) && referenceDate.valueOf() - curr.date.valueOf() >= 0 ? curr : prev,
  );
};

export const nearestDateInFuture = (dataArray: ParsedDataProps[], referenceDate: Date): ParsedDataProps | null => {
  if (!dataArray || dataArray.length === 0) {
    return null;
  }
  return dataArray.reduce((prev, curr) =>
    Math.abs(referenceDate.valueOf() - curr.date.valueOf()) < Math.abs(referenceDate.valueOf() - prev.date.valueOf()) && referenceDate.valueOf() - curr.date.valueOf() <= 0 ? curr : prev,
  );
};

export const verifyData = async (cutData: ParsedDataProps[], xDomain: XDomain, origData: ParsedDataProps[]) => {
  if (!cutData || cutData.length === 0) {
    const closestLower = nearestDateInPast(origData, xDomain[0]);
    const closestUpper = nearestDateInFuture(origData, xDomain[1]);
    return Array.from(new Set([closestLower, closestUpper].filter(Boolean)));
  }

  const closestLower = cutData[0].date.valueOf() === xDomain[0].valueOf() ? cutData[0] : nearestDateInPast(origData, xDomain[0]);
  const closestUpper = cutData[cutData.length - 1].date.valueOf() === xDomain[1].valueOf() ? cutData[cutData.length - 1] : nearestDateInFuture(origData, xDomain[1]);

  return Array.from(new Set([closestLower, ...cutData, closestUpper]));
};

export const filterData = async (data: ParsedDataProps[], xDomain: XDomain, xRange: XRange): Promise<{ verifiedFilteredData: ParsedDataProps[]; verifiedXDomain: XDomain }> => {
  if (!data || data.length === 0) {
    return {
      verifiedFilteredData: [],
      verifiedXDomain: [null, null] as XDomain,
    };
  }

  const cutData = await asyncFrame(() => data.filter((d) => d.date.valueOf() >= xDomain[0].valueOf() && d.date.valueOf() <= xDomain[1].valueOf()));

  const verifiedData = await verifyData(cutData, xDomain, data);

  const dataPerPixel = verifiedData.length / (xRange[1] - xRange[0]);
  const verifiedFilteredData = verifiedData.filter((_d, i) => i === 0 || i === verifiedData.length - 1 || i % Math.ceil(dataPerPixel) === 0);

  return {
    verifiedFilteredData,
    verifiedXDomain: D3Array.extent(verifiedFilteredData, (dataPoint) => dataPoint.date),
  };
};

export const calibrateData = (calibrationSet: ParsedDataProps, data: ParsedDataProps[]): ParsedDataProps[] | null =>
  data
    ? data.map((d: ParsedDataProps) => {
        const calibrated = {
          ...d,
          performance: (d.performance / calibrationSet.performance) * 100,
        };

        if (d.benchmark) {
          calibrated.benchmark = (d.benchmark / calibrationSet.benchmark) * 100;
        }
        return calibrated;
      })
    : null;

export const dataProcessingPipeline = async (parsedData: ParsedDataProps[], xDomain: XDomain, xRange: XRange, hasBenchmark: boolean) => {
  const { verifiedFilteredData, verifiedXDomain } = await asyncFrame(() => filterData(parsedData, xDomain, xRange));
  const percentifiedData = await asyncFrame(() => percentifyData(verifiedFilteredData, hasBenchmark));
  const calibratedData = await asyncFrame(() => calibrateData(percentifiedData[0], percentifiedData));

  return {
    processedData: calibratedData,
    verifiedXDomain,
  };
};

export const changeClassNameForChartHighlighting = (selector: HighlightLinePresets): string => {
  let performanceDetailChartClassSuffix = '';
  if (selector === HighlightLinePresets.PERFORMANCE) {
    performanceDetailChartClassSuffix = ' performanceDetailChart-highlightPerformance';
  } else if (selector === HighlightLinePresets.BENCHMARK) {
    performanceDetailChartClassSuffix = ' performanceDetailChart-highlightBenchmark';
  }

  return performanceDetailChartClassSuffix;
};

export const calcYDomain = (data: ParsedDataProps[], hasBenchmark: boolean): YDomain => {
  return [
    D3Array.min(data, (d) => {
      return hasBenchmark ? D3Array.min([d.performance, d.benchmark]) : d.performance;
    }),
    D3Array.max(data, (d) => {
      return hasBenchmark ? D3Array.max([d.performance, d.benchmark]) : d.performance;
    }),
  ];
};

export const getAllApplyingDatePresets = (xDomain: XDomain, startDate: Date, endDate: Date): DatePresets[] | null => {
  if (moment(xDomain[1]).startOf('day').valueOf() !== moment(endDate).startOf('day').valueOf()) {
    return null;
  }

  const availableDatePresets = datePresets(xDomain);

  return (
    availableDatePresets.reduce<DatePresets[]>((prev, curr, index) => {
      let currentDate: any = curr;

      if (typeof curr === 'number') {
        currentDate = moment.unix(curr);
      }

      if (moment(startDate).startOf('day').valueOf() === moment(currentDate).startOf('day').valueOf()) {
        prev.push(index);
      }
      return prev;
    }, []) || null
  );
};
